import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { validateCodicePersona, validatePassword, sanitizeInput } from "@/shared/utils";
import { LoginFormData } from "@/features/auth/types";

interface LoginFormProps {
  onLogin: (data: LoginFormData) => void;
  onLoginError: (error: string) => void;
  isLoading?: boolean;
  error?: string;
}

export function LoginForm({ onLogin, onLoginError, isLoading = false, error }: LoginFormProps) {
  const [formData, setFormData] = useState<LoginFormData>({
    codicePersona: "",
    password: "",
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<LoginFormData>>({});
  const [reauthMessage, setReauthMessage] = useState<string>("");

  // Check for re-auth message on component mount
  useEffect(() => {
    const message = sessionStorage.getItem("reauthMessage");
    if (message) {
      setReauthMessage(message);
      // Clear the message after displaying it
      sessionStorage.removeItem("reauthMessage");
    }
  }, []);

  const validateForm = (): boolean => {
    const errors: Partial<LoginFormData> = {};

    if (!validateCodicePersona(formData.codicePersona)) {
      errors.codicePersona = "Please enter a valid codice persona";
    }

    if (!validatePassword(formData.password)) {
      errors.password = "Password must be at least 8 characters";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const sanitizedData = {
      codicePersona: sanitizeInput(formData.codicePersona),
      password: formData.password, // Don't sanitize password
    };

    onLogin(sanitizedData);
  };

  const handleInputChange = (field: keyof LoginFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeInput(e.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

    // Clear general error when user starts typing
    if (error) {
      onLoginError("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">360welfare Token Sharing</CardTitle>
          <CardDescription className="text-center">
            Login with your Polimi credentials to access the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="codicePersona">Codice Persona</Label>
              <Input
                id="codicePersona"
                type="text"
                placeholder="Enter your Codice Persona"
                value={formData.codicePersona}
                onChange={handleInputChange("codicePersona")}
                disabled={isLoading}
                required
                aria-invalid={!!fieldErrors.codicePersona}
              />
              {fieldErrors.codicePersona && (
                <p className="text-sm text-destructive">{fieldErrors.codicePersona}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange("password")}
                disabled={isLoading}
                required
                aria-invalid={!!fieldErrors.password}
              />
              {fieldErrors.password && (
                <p className="text-sm text-destructive">{fieldErrors.password}</p>
              )}
            </div>

            {reauthMessage && (
              <Alert variant="default" className="border-blue-200 bg-blue-50 text-blue-800">
                <AlertDescription>{reauthMessage}</AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Authenticating..." : "Login"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>Your credentials are used to authenticate with Polimi and 360welfare systems.</p>
            <p>We securely store encrypted session cookies for token generation.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}